<template>
  <section id="portfolio" class="portfolio-section">
    <VueEasyLightbox
      :visible="visible"
      :imgs="imgs"
      :index="index"
      @hide="handleHide"
    />

    <div class="section-title">
      <h2 class="title">{{ $t('sections.portfolio') }}</h2>
    </div>

    <TransitionGroup
      name="projects"
      tag="div"
      class="projects"
    >
      <article
        v-for="project in visibleProjects"
        :key="project.id"
        class="project"
      >
        <div class="about">
          <h3 class="title">{{ pick(project.i18n).title }}</h3>
          <p class="text">{{ pick(project.i18n).text }}</p>
          <p class="tecnology">{{ pick(project.i18n).technology }}</p>

          <div class="sep" />

          <div class="links">
            <a
              v-if="project.images?.length"
              href="#"
              @click.prevent="showGallery(project.images)"
            >
              {{ $t('portfolio.seeMore') }}
            </a>

            <a
              v-if="project.link"
              :href="project.link"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ $t('portfolio.open') }}
            </a>
          </div>
        </div>

        <p class="name">{{ pick(project.i18n).title }}</p>

        <img
          :src="project.image"
          :alt="pick(project.i18n).title"
          loading="lazy"
        >
      </article>
    </TransitionGroup>
  </section>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import VueEasyLightbox from 'vue-easy-lightbox';
import { useLocalized } from '@/composables/useLocalized';
import { projects } from '@/data/projects';

const { pick } = useLocalized();
const visibleProjects = projects;

const visible = ref(false);
const imgs = ref<string[]>([]);
const index = ref(0);

function showGallery(images: string[]) {
  imgs.value = images;
  index.value = 0;
  visible.value = true;
}

function handleHide() {
  visible.value = false;
}
</script>

<style scoped lang="scss">
.portfolio-section {
  scroll-margin-top: 80px;

  :deep(.vel-img-modal),
  :deep(.vel-modal) {
    background: rgba(0, 0, 0, 0.9) !important;
  }
}

.projects {
  display: flex;
  flex-wrap: wrap;

  &-enter-active,
  &-leave-active {
    transition: opacity 0.3s ease;
  }

  &-enter-from,
  &-leave-to {
    opacity: 0;
  }

  .project {
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow: hidden;
    width: calc(100% / 3);
    height: 100%;
    min-height: 450px;
    position: relative;
    transition: all 0.3s ease;

    @media (max-width: 1440px) {
      min-height: 350px;
    }

    @media (max-width: 1024px) {
      width: calc(100% / 2);
      min-height: 350px;
    }

    @media (max-width: 768px) {
      width: 100%;
    }

    &:hover {
      img {
        filter: blur(5px);
        transform: translate(-50%, -50%) scale(1.2);
      }

      .name {
        opacity: 0;
      }

      .about {
        opacity: 1;
        transform: scale(1);
      }
    }

    .name {
      position: absolute;
      z-index: 10;
      bottom: 0;
      left: 0;
      display: block;
      width: 100%;
      padding: 10px;
      margin: 0;
      background: rgba(0, 0, 0, 0.5);
      text-align: center;
      opacity: 1;
      transition: all 0.3s ease;
    }

    img {
      width: 100%;
      height: 100%;
      position: absolute;
      z-index: 0;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      transition: all 1s ease;
    }

    .about {
      position: absolute;
      z-index: 5;
      top: 0;
      left: 0;
      padding: 20px;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.8);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      opacity: 0;
      transform: scale(1.2);
      transition: all 0.3s ease;

      .title {
        display: table;
        background: #b4d455;
        padding: 10px;
        margin: 15px;
        color: #000;
        font-size: 20px;
        text-align: center;
        font-weight: 600;
        text-transform: uppercase;
      }

      .text {
        font-size: 16px;
        text-align: center;
        margin: 15px;
      }

      .tecnology {
        font-size: 16px;
        text-align: center;
        font-weight: 600;
        text-transform: uppercase;
      }

      .links {
        display: flex;
        justify-content: center;
        align-items: center;
        flex-wrap: wrap;

        a {
          background: #ffae00;
          padding: 5px 10px;
          margin: 5px;
          cursor: pointer;
          font-size: 22px;
          text-align: center;
          font-weight: 600 !important;
          color: #000;
          border-radius: 5px;
          transition: all 0.3s ease;

          &:hover {
            transform: scale(1.1);
          }
        }
      }
    }
  }
}
</style>
